import * as THREE from 'three';

/**
 * landmarks/common.ts — the tiny modelling toolkit every city's landmark module
 * shares: flat-shaded primitives, textured sign panels, one-draw-call repeats,
 * and canvas-texture helpers. Keeping these here means each city file is pure
 * model code.
 */

/** How a landmark sits relative to the track (see Landmarks.buildLandmark). */
export interface LandmarkDef {
  /** Build the model facing local +Z ("front"). `halfWidth` is the road's
   *  half-width — span landmarks use it to clear the racing line. */
  build: (halfWidth: number) => THREE.Group;
  /** 'side'   = beside the road, offset beyond the guardrail, facing the road;
   *  'span'   = centred ON the road, aligned to travel (drive-through/overpass);
   *  'finish' = centred past the COURSE END, facing back at the racers. */
  placement: 'side' | 'span' | 'finish';
  /** side: lateral distance beyond the road half-width (default 10). */
  offset?: number;
  /** finish: how far past the COURSE END (the last road node) it stands
   *  (default 30). The end barrier sits 12 past that node — keep the model's
   *  near face beyond it (ahead ≥ 12 + the model's half-depth) so finished
   *  cars never coast through the structure (M97, measured per model). */
  ahead?: number;
}

export function mat(color: number, emissive = 0, rough = 0.8): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive,
    emissiveIntensity: emissive ? 0.9 : 0,
    flatShading: true,
    roughness: rough,
  });
}

export function box(
  w: number,
  h: number,
  d: number,
  color: number,
  x = 0,
  y = 0,
  z = 0,
  emissive = 0,
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color, emissive));
  m.position.set(x, y, z);
  return m;
}

export function cyl(
  rt: number,
  rb: number,
  h: number,
  seg: number,
  color: number,
  x = 0,
  y = 0,
  z = 0,
  emissive = 0,
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), mat(color, emissive));
  m.position.set(x, y, z);
  return m;
}

/** A flat textured panel facing +Z (for signs / glowing screens). */
export function panel(w: number, h: number, tex: THREE.Texture, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(w, h),
    new THREE.MeshBasicMaterial({ map: tex, toneMapped: false }),
  );
  m.position.set(x, y, z);
  return m;
}

/** Repeat one small part at many positions in a single draw call (e.g. balusters). */
export function instancedAt(
  geo: THREE.BufferGeometry,
  color: number,
  positions: [number, number, number][],
): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geo, mat(color), positions.length);
  const m = new THREE.Matrix4();
  positions.forEach((p, i) => {
    m.makeTranslation(p[0], p[1], p[2]);
    mesh.setMatrixAt(i, m);
  });
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

/** A 2D canvas + context for drawing a sign/texture (throws if 2D is missing). */
export function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Landmarks: no 2D context');
  return [c, ctx];
}

export function canvasTexture(c: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
